import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Link } from 'react-router-dom';

import countries from './countries';
import { useTheme } from 'contexts/ThemeContext';

import './PaymentForm.css';

const PaymentForm = (props) => {
    const stripe = useStripe();
    const elements = useElements();
    const { isDark } = useTheme();
    const [error, setError] = useState(null);
    const [name, setName] = useState("");
    const [address, setAddress] = useState({
      line1: "",
      postal_code: "",
      city: "",
      country: "",
    });
    const [vatNumber, setVatNumber] = useState("");

    const cardElementOptions = {
        hidePostalCode: true,
        style: {
            base: {
                fontSize: '16px',
                color: isDark ? '#ffffff' : '#424770',
                fontFamily: 'inherit',
                backgroundColor: 'transparent',
                '::placeholder': {
                    color: isDark ? '#b0b0b0' : '#aab7c4',
                },
                lineHeight: '1.5',
            },
            invalid: {
                color: isDark ? '#f44336' : '#9e2146',
            },
        },
    };

    // Force la mise à jour de l'élément Stripe quand le thème change
    useEffect(() => {
        if (elements) {
            const cardElement = elements.getElement(CardElement);
            if (cardElement) {
                cardElement.update(cardElementOptions);
            }
        }
    }, [isDark, elements, cardElementOptions]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        if (props.onPaymentInitialized) {
            props.onPaymentInitialized();
        }

        if (!stripe || !elements) {
            return;
        }

        if (!address.country) {
            if (props.onPaymentError) {
                props.onPaymentError();
            }
            setError('Veuillez sélectionner un pays.');
            return;
        }

        const cardElement = elements.getElement(CardElement);
        const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: {
              name,
              address,
            },
          });

        if (error) {
            if (props.onPaymentError) {
                props.onPaymentError();
            }
            setError(error.message);
        } else {
            setError(null);
            if (props.onPaymentSuccessfull) {
                props.onPaymentSuccessfull(name, address, vatNumber, paymentMethod.id);
            }
        }
    };

    const sortedCountries = [...countries].sort((a, b) =>
        a.name.localeCompare(b.name, "fr", { sensitivity: "base" })
    );

    return (
        <div className="payment-form-container">
            <form onSubmit={handleSubmit} id="payment-form">
                <input
                    className="text-input"
                    placeholder='Nom complet *'
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                />

                <select
                    className="text-input"
                    value={address.country}
                    onChange={(e) =>setAddress({ ...address, country: e.target.value })}
                    required
                >
                    <option value="">Pays *</option>
                    {sortedCountries.map((country) => (
                    <option key={country.code} value={country.code}>
                        {`${country.flag} ${country.name}`}
                    </option>
                    ))}
                </select>

                <input
                    className="text-input"
                    placeholder='Ville *'
                    value={address.city}
                    onChange={(e) => setAddress({ ...address, city: e.target.value })}
                    required
                />

                <input
                    className="text-input"
                    placeholder='Code postal *'
                    value={address.postal_code}
                    onChange={(e) => setAddress({ ...address, postal_code: e.target.value })}
                    required
                />

                <input
                    className="text-input"
                    placeholder='Adresse (optionnelle)'
                    value={address.line1}
                    onChange={(e) => setAddress({ ...address, line1: e.target.value })}
                />

                <input
                    className="text-input"
                    placeholder='Numéro de TVA (optionnel)'
                    value={vatNumber}
                    onChange={(e) => setVatNumber(e.target.value)}
                />

                <div className="card-number text-input">
                    <CardElement options={cardElementOptions} />
                </div>

                {error && <div className="error-message">{error}</div>}

                <p className="legal-disclaimer">
                    En souscrivant à un abonnement, vous acceptez nos <Link to="/cgv" className="legal-link">CGV</Link> et nos <Link to="/cgu" className="legal-link">CGU</Link>.
                </p>
            </form>
        </div>
    );
};

export default PaymentForm;
